import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import type {
    ChartAccount,
    LocaleCode,
    OperationPreview,
    RecordableOperation,
    RecordedOperation,
} from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { AccessNotice } from '../components';
import type { OperationsAccess } from '../operationsAccess';
import {
    CommandPicker,
    CommandPickerSkeleton,
    DateBand,
    ImpactPanel,
    NoOperations,
    PickedOperation,
    RecordForm,
    RecordedNotice,
} from './components';
import type {
    DateStatus,
    EntryShapeRow,
    NoOperationsCopy,
    PeriodNotice,
    RecordError,
    RecordForm as RecordFormState,
} from './recordMeta';

type RecordPageViewProps = {
    access: OperationsAccess;
    canRecord: boolean;
    locale: LocaleCode;
    isLoading: boolean;
    operations: RecordableOperation[];
    dateStatus: DateStatus | null;
    noOperationsCopy: NoOperationsCopy | null;
    canEditTemplates: boolean;
    onChangeOperation: (() => void) | null;
    shape: EntryShapeRow[];
    isBalanced: boolean;
    periodLabel: string | null;
    /** The amount as typed, formatted — carried on the Post button. */
    postAmountLabel: string | null;
    jumpTo: string | null;
    onDateChange: (date: string) => void;
    onJumpToEarliest: () => void;
    onToday: () => void;
    onYesterday: () => void;
    periodNotice: PeriodNotice | null;
    dateError: string | null;
    selected: RecordableOperation | null;
    onSelect: (key: string) => void;
    form: RecordFormState;
    onFieldChange: (patch: Partial<RecordFormState>) => void;
    onSlotChange: (lineId: string, accountId: string) => void;
    accounts: ChartAccount[];
    preview: OperationPreview | null;
    isPreviewing: boolean;
    amountError: 'invalid' | null;
    formError: RecordError | null;
    canPost: boolean;
    isPosting: boolean;
    onPost: () => void;
    recorded: RecordedOperation | null;
    onDismissRecorded: () => void;
};

/**
 * The Record tab — render only.
 *
 * Two columns from `lg` up: the date, the picker and the form on the left, the
 * ledger impact on the right, so what is being filled in and what it will book
 * are on screen together. Below `lg` they stack with the impact **second** — it
 * is what the user checks, not what they manipulate — and the Post button sits
 * under it either way, because the button belongs to the entry rather than to
 * the form.
 *
 * **The date leads and appears exactly once.** It resolves the recordable list
 * server-side, so it cannot sit inside the form whose shape it decides. When it
 * leaves nothing to pick, the empty state takes the *picker's* place rather than
 * appearing under an empty grid — the impact column does not move either way.
 *
 * **The picker's slot has four occupants and only ever one of them**: the
 * skeleton while the list is unknown, the empty state when the date leaves
 * nothing, the **collapsed header** once an operation is picked, and the
 * search-and-list picker otherwise. They are ordered here by how much they know:
 * a loading list must not be read as an empty one, and an empty one must not be
 * read as a choice already made.
 *
 * **The form takes the picker's place, it does not sit under it.** That is what
 * makes the screen the same height at nine operations and at a hundred — the
 * fields a user needs after picking are where the list they picked from was, not
 * below it. The impact column does not move in either state, which is the whole
 * reason this layout works.
 *
 * The View renders no error state of its own. A failed *read* is thrown by the
 * container's content child and owned by the boundary; a *refusal* is not an
 * error state at all — it is an answer about this request, and it renders on the
 * field that caused it.
 */
export const RecordPageView: React.FC<RecordPageViewProps> = ({
    access,
    canRecord,
    locale,
    isLoading,
    operations,
    dateStatus,
    noOperationsCopy,
    canEditTemplates,
    onChangeOperation,
    shape,
    isBalanced,
    periodLabel,
    postAmountLabel,
    jumpTo,
    onDateChange,
    onJumpToEarliest,
    onToday,
    onYesterday,
    periodNotice,
    dateError,
    selected,
    onSelect,
    form,
    onFieldChange,
    onSlotChange,
    accounts,
    preview,
    isPreviewing,
    amountError,
    formError,
    canPost,
    isPosting,
    onPost,
    recorded,
    onDismissRecorded,
}) => {
    const { t } = useTranslation('pages');

    if (!canRecord) {
        return (
            <section
                aria-label={t('operations.record.title')}
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('operations-record-section')}
            >
                <AccessNotice
                    access={
                        access as Exclude<OperationsAccess, { kind: 'full' }>
                    }
                />
            </section>
        );
    }

    return (
        <section
            aria-label={t('operations.record.title')}
            className="flex flex-col gap-5"
            {...getNodeTestIdProp('operations-record-section')}
        >
            <p
                className="text-sm text-muted"
                {...getNodeTestIdProp('operations-record-intro')}
            >
                {t('operations.record.intro')}
            </p>

            {recorded ? (
                <RecordedNotice
                    onDismiss={onDismissRecorded}
                    recorded={recorded}
                />
            ) : null}

            {formError && formError.target === 'operation' ? (
                <p
                    className="rounded-xl bg-danger-soft px-4 py-3 text-sm text-danger-soft-foreground"
                    {...getNodeTestIdProp('operations-record-operation-error')}
                >
                    {formError.message}
                </p>
            ) : null}

            {formError && formError.target === 'form' ? (
                <p
                    className="rounded-xl bg-danger-soft px-4 py-3 text-sm text-danger-soft-foreground"
                    {...getNodeTestIdProp('operations-record-form-error')}
                >
                    {formError.message}
                </p>
            ) : null}

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                <div className="flex flex-col gap-4">
                    <DateBand
                        date={form.date}
                        dateError={dateError}
                        isDisabled={isPosting}
                        onDateChange={onDateChange}
                        onToday={onToday}
                        onYesterday={onYesterday}
                        periodNotice={periodNotice}
                        status={dateStatus}
                    />

                    {isLoading ? (
                        <CommandPickerSkeleton />
                    ) : noOperationsCopy ? (
                        <NoOperations
                            copy={noOperationsCopy}
                            jumpTo={jumpTo}
                            onJump={onJumpToEarliest}
                        />
                    ) : selected ? (
                        <PickedOperation
                            locale={locale}
                            onChange={onChangeOperation}
                            operation={selected}
                        />
                    ) : (
                        <CommandPicker
                            canEditTemplates={canEditTemplates}
                            locale={locale}
                            onSelect={onSelect}
                            operations={operations}
                        />
                    )}

                    {selected ? (
                        <RecordForm
                            accounts={accounts}
                            amountError={amountError}
                            form={form}
                            formError={formError}
                            isDisabled={isPosting}
                            locale={locale}
                            onFieldChange={onFieldChange}
                            onSlotChange={onSlotChange}
                            operation={selected}
                        />
                    ) : null}
                </div>

                <div className="flex flex-col gap-4">
                    <ImpactPanel
                        formError={formError}
                        isBalanced={isBalanced}
                        isPreviewing={isPreviewing}
                        periodLabel={periodLabel}
                        preview={preview}
                        shape={shape}
                        version={selected?.version ?? null}
                    />
                    <div className="flex flex-row gap-2">
                        <Button
                            // Disabled until the server has said what this will
                            // post. The gate is a successful preview rather than the
                            // form's own opinion, because posting is immediate and
                            // there is no draft to fix afterwards.
                            isDisabled={!canPost}
                            onPress={onPost}
                            variant="primary"
                            {...getNodeTestIdProp('operations-record-post')}
                        >
                            <Icon aria-hidden icon="gravity-ui:check" />
                            {isPosting
                                ? t('operations.record.posting')
                                : postAmountLabel
                                  ? t('operations.record.postAmount', {
                                        amount: postAmountLabel,
                                    })
                                  : t('operations.record.post')}
                        </Button>
                        {/* The same exit Change offers, where people were
                            looking for it. Rendered on the same condition, so
                            the two can never disagree: `null` means this
                            session may record exactly one operation, and there
                            is no picker to go back to. Subordinate to Post —
                            it is the way out, not the act. */}
                        {selected && onChangeOperation ? (
                            <Button
                                isDisabled={isPosting}
                                onPress={onChangeOperation}
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    'operations-record-cancel',
                                )}
                                className="ml-auto"
                            >
                                {t('operations.record.cancel')}
                            </Button>
                        ) : null}
                    </div>
                </div>
            </div>
        </section>
    );
};
